import React, { useEffect, useState, useCallback } from 'react';
import api from '../utils/api';
import { Btn, Badge, Modal, Input, Select, Loading, EmptyState } from '../components/ui';

export default function FactoryMaster() {
  const [factories, setFactories] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterDivision, setFilterDivision] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', code: '', location: '', division_id: '', status: 'Active' });
  const [error, setError] = useState('');

  useEffect(() => { document.getElementById('lc-page-title').textContent = 'Factory master'; }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [f, d] = await Promise.all([api.get('/factories'), api.get('/divisions')]);
      setFactories(f.data);
      setDivisions(d.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openAdd = () => {
    setEditing(null);
    setForm({ name: '', code: '', location: '', division_id: filterDivision || '', status: 'Active' });
    setError(''); setModalOpen(true);
  };

  const openEdit = (f) => {
    setEditing(f);
    setForm({ name: f.name, code: f.code, location: f.location, division_id: f.division_id, status: f.status });
    setError(''); setModalOpen(true);
  };

  const handleRemove = async (f) => {
    if (!window.confirm(`Remove factory "${f.name}"? This cannot be undone.`)) return;
    try { await api.delete(`/factories/${f.id}`); fetchAll(); }
    catch (e) { alert(e.response?.data?.error || 'Error removing factory'); }
  };

  const handleSave = async () => {
    if (!form.name || !form.code || !form.location) { setError('Name, code and location are required'); return; }
    try {
      if (editing) await api.put(`/factories/${editing.id}`, form);
      else await api.post('/factories', form);
      setModalOpen(false); fetchAll();
    } catch (e) { setError(e.response?.data?.error || 'Error saving'); }
  };

  const statusColor = s => s === 'Active' ? 'green' : s === 'Setup' ? 'amber' : 'red';

  // Filter factories by selected division
  const filtered = factories.filter(f =>
    !filterDivision || String(f.division_id) === String(filterDivision)
  );

  return (
    <div style={{ padding: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 500 }}>Factory master <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--text-secondary)', marginLeft: 6 }}>{filtered.length} records</span></div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Manufacturing units registered under divisions</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Select
            value={filterDivision}
            onChange={setFilterDivision}
            placeholder="All divisions"
            options={divisions.map(d => ({ value: d.id, label: d.name }))}
          />
          <Btn variant="primary" size="sm" onClick={openAdd}>+ Add new</Btn>
        </div>
      </div>

      <div style={{ border: '0.5px solid var(--border-md)', borderRadius: 10, overflow: 'hidden', background: 'var(--bg-primary)' }}>
        {loading ? <Loading /> : filtered.length === 0
          ? <EmptyState message="No factories found" action={<Btn variant="primary" onClick={openAdd}>+ Add factory</Btn>} />
          : (
            <table className="lc-table">
              <thead>
                <tr><th>Factory name</th><th>Code</th><th>Location</th><th>Division</th><th>Status</th><th></th></tr>
              </thead>
              <tbody>
                {filtered.map(f => (
                  <tr key={f.id}>
                    <td style={{ fontWeight: 500 }}>{f.name}</td>
                    <td><span style={{ fontFamily: 'monospace', fontSize: 12, background: 'var(--bg-secondary)', padding: '2px 6px', borderRadius: 4 }}>{f.code}</span></td>
                    <td style={{ color: 'var(--text-secondary)' }}>{f.location}</td>
                    <td>{f.division_name || <span style={{ color: 'var(--text-tertiary)' }}>—</span>}</td>
                    <td><Badge type={statusColor(f.status)}>{f.status}</Badge></td>
                    <td style={{ display: 'flex', gap: 12 }}>
                    <a style={{ fontSize: 11, color: 'var(--acc)', cursor: 'pointer' }} onClick={() => openEdit(f)}>Edit</a>
                    <a style={{ fontSize: 11, color: 'var(--red)', cursor: 'pointer' }} onClick={() => handleRemove(f)}>Remove</a>
                  </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Edit factory' : 'Add factory'} width={400}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[
            { label: 'Factory name', key: 'name', placeholder: 'e.g. Rajan Textiles Unit 1' },
            { label: 'Factory code', key: 'code', placeholder: 'e.g. RT-U1' },
            { label: 'Location', key: 'location', placeholder: 'e.g. Bangalore, Karnataka' },
          ].map(f => (
            <div key={f.key}>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>{f.label}</div>
              <Input value={form[f.key]} onChange={v => setForm(p => ({ ...p, [f.key]: v }))} placeholder={f.placeholder} style={{ width: '100%' }} />
            </div>
          ))}
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Division</div>
            <Select value={form.division_id} onChange={v => setForm(p => ({ ...p, division_id: v }))}
              placeholder="Select division…" options={divisions.map(d => ({ value: d.id, label: d.name }))} style={{ width: '100%' }} />
          </div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Status</div>
            <Select value={form.status} onChange={v => setForm(p => ({ ...p, status: v }))}
              options={[{ value: 'Active', label: 'Active' }, { value: 'Setup', label: 'Setup' }, { value: 'Inactive', label: 'Inactive' }]}
              style={{ width: '100%' }} />
          </div>
          {error && <div style={{ fontSize: 11.5, color: 'var(--red)', background: 'var(--red-bg)', padding: '6px 9px', borderRadius: 6 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
            <Btn onClick={() => setModalOpen(false)}>Cancel</Btn>
            <Btn variant="primary" onClick={handleSave}>Save</Btn>
          </div>
        </div>
      </Modal>
    </div>
  );
}